import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import {
  fitCameraDistance,
  meshColor,
  normalizeModelFormat,
  resolveViewerPlan,
  supportsToggleVisible,
  type ViewerPhase,
} from './model3dView';

/* ============================================================
   Model3DPreview — cc4909b4 (Artem 2026-08-28 §8): interactive
   3D model preview with the ME-2 PNG as fallback.

   Drop-in replacement for the previous preview <img> blocks:
   renders the live mesh (three.js, client-side parse of the raw
   bytes from GET /models/{id}/content) for STL/OBJ/3MF with
   WebGL, and keeps the rendered PNG for STEP, no-WebGL devices
   and load failures — the preview never degrades the page.

   - orbit/zoom/pan with touch (OrbitControls), damped;
   - the mesh is painted with the selected color (provider hex
     swatch → palette name → neutral);
   - the supports toggle appears only when real support
     geometry/estimate data is attached (none exists yet — the
     hook stays hidden, no fake data);
   - three.js loads lazily (dynamic import) so the main bundle
     stays lean; everything is disposed on unmount.
   - cca635b3: besides the party-scoped fileId source, the preview accepts
     a public /media/… modelUrl (shop product galleries) — bytes are fetched
     anonymously from the static media surface.
   ============================================================ */

export interface Model3DPreviewProps {
  /** Party-scoped model id (GET /models/{id}/content) — the order-flow source. */
  fileId?: string | null;
  /** cca635b3: public /media/… URL of a mesh (shop product gallery rows).
   * Used only when fileId is absent; bytes are fetched anonymously. */
  modelUrl?: string | null;
  /** Numeric or named FileFormat from the DTO; unknown → PNG fallback. */
  format?: number | string | null;
  previewUrl?: string | null;
  fileName?: string | null;
  /** Chosen print color name (palette/stock) for the mesh tint. */
  colorName?: string | null;
  /** Provider hex swatch for colorName — wins over the palette map. */
  colorHex?: string | null;
  /** Support geometry/estimate hook (cc4909b4 §8) — null keeps the toggle hidden. */
  supports?: unknown;
  alt: string;
  /** Pixel height of the viewer canvas (width follows the container). */
  height?: number;
  style?: CSSProperties;
}

type LoadState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'fallback' };

let webglSupported: boolean | null = null;

function detectWebgl(): boolean {
  if (webglSupported != null) return webglSupported;
  try {
    const canvas = document.createElement('canvas');
    webglSupported = !!(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    webglSupported = false;
  }
  return webglSupported;
}

export function Model3DPreview({
  fileId,
  modelUrl,
  format,
  previewUrl,
  fileName,
  colorName,
  colorHex,
  supports,
  alt,
  height = 220,
  style,
}: Model3DPreviewProps) {
  const plan = useMemo(
    () => resolveViewerPlan({
      format: normalizeModelFormat(format),
      previewUrl,
      webglOk: typeof document === 'undefined' ? false : detectWebgl(),
    }),
    [format, previewUrl],
  );

  const [state, setState] = useState<LoadState>({ kind: 'idle' });
  const [showSupports, setShowSupports] = useState(false);
  const canvasHostRef = useRef<HTMLDivElement | null>(null);
  const phase: ViewerPhase = state.kind;

  const paintColor = useMemo(() => meshColor({ hex: colorHex, colorName }), [colorHex, colorName]);

  useEffect(() => { setState({ kind: 'idle' }); }, [fileId, modelUrl]);

  // ── Live mesh mount ────────────────────────────────────────
  useEffect(() => {
    if (plan !== '3d' || (!fileId && !modelUrl)) return;
    let disposed = false;
    let cleanup: (() => void) | null = null;
    setState({ kind: 'loading' });

    (async () => {
      try {
        const [THREE, controlsMod, bytes] = await Promise.all([
          import('three'),
          import('three/examples/jsm/controls/OrbitControls.js'),
          fileId
            ? api.fetchModelContent(fileId)
            : fetch(resolveMediaUrl(modelUrl!)).then((res) => {
                if (!res.ok) throw new Error(`model-url-${res.status}`);
                return res.arrayBuffer();
              }),
        ]);
        if (disposed) return;
        const { OrbitControls } = controlsMod;

        const fmt = normalizeModelFormat(format);
        let geometry: import('three').BufferGeometry;
        if (fmt === 0) {
          const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
          geometry = new STLLoader().parse(bytes);
        } else if (fmt === 1) {
          const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
          const text = new TextDecoder().decode(bytes);
          const group = new OBJLoader().parse(text);
          // OBJ can carry several meshes — merge their position attributes.
          const merged = new THREE.BufferGeometry();
          const positions: number[] = [];
          group.traverse((child) => {
            const pos = (child as import('three').Mesh).geometry?.getAttribute('position');
            if (pos) for (let i = 0; i < pos.count * 3; i++) positions.push(pos.array[i] as number);
          });
          if (positions.length === 0) throw new Error('obj-empty');
          merged.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
          merged.computeVertexNormals();
          geometry = merged;
        } else {
          const { ThreeMFLoader } = await import('three/examples/jsm/loaders/3MFLoader.js');
          const object = new ThreeMFLoader().parse(bytes) as import('three').Group;
          const merged = new THREE.BufferGeometry();
          const positions: number[] = [];
          object.traverse((child) => {
            const pos = (child as import('three').Mesh).geometry?.getAttribute('position');
            if (pos) for (let i = 0; i < pos.count * 3; i++) positions.push(pos.array[i] as number);
          });
          if (positions.length === 0) throw new Error('3mf-empty');
          merged.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
          merged.computeVertexNormals();
          geometry = merged;
        }

        const host = canvasHostRef.current;
        if (!host || disposed) { geometry.dispose(); return; }

        const scene = new THREE.Scene();
        scene.background = new THREE.Color('#F2F2F7');

        // Center the mesh on the origin and read its real size (mm).
        geometry.computeBoundingBox();
        const box = geometry.boundingBox!;
        const center = box.getCenter(new THREE.Vector3());
        geometry.translate(-center.x, -center.y, -center.z);
        geometry.computeBoundingBox();
        const size = box.getSize(new THREE.Vector3());

        const material = new THREE.MeshStandardMaterial({
          color: new THREE.Color(paintColor),
          roughness: 0.55,
          metalness: 0.05,
          flatShading: true,
        });
        const mesh = new THREE.Mesh(geometry, material);
        scene.add(mesh);

        scene.add(new THREE.HemisphereLight('#ffffff', '#d8dce4', 1.0));
        const key = new THREE.DirectionalLight('#ffffff', 1.6);
        key.position.set(1, 1.4, 1.2);
        scene.add(key);
        const fill = new THREE.DirectionalLight('#ffffff', 0.5);
        fill.position.set(-1, -0.4, -0.8);
        scene.add(fill);

        const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 1e6);
        const fit = fitCameraDistance(size, camera.fov);
        camera.near = Math.max(fit.radius * 0.01, 0.1);
        camera.far = fit.distance * 20;
        camera.position.set(fit.distance * 0.7, fit.distance * 0.55, fit.distance * 0.9);
        camera.lookAt(0, 0, 0);

        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio ?? 1, 2));
        host.appendChild(renderer.domElement);
        renderer.domElement.style.width = '100%';
        renderer.domElement.style.height = `${height}px`;
        renderer.domElement.style.display = 'block';
        renderer.domElement.style.touchAction = 'none'; // OrbitControls owns touch

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.08;
        controls.minDistance = fit.radius * 1.2;
        controls.maxDistance = fit.distance * 4;

        const resize = () => {
          const w = host.clientWidth || 1;
          renderer.setSize(w, height, false);
          camera.aspect = w / height;
          camera.updateProjectionMatrix();
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(host);

        let raf = 0;
        const tick = () => {
          controls.update();
          renderer.render(scene, camera);
          raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);

        cleanup = () => {
          cancelAnimationFrame(raf);
          observer.disconnect();
          controls.dispose();
          geometry.dispose();
          material.dispose();
          renderer.dispose();
          renderer.domElement.remove();
        };

        if (!disposed) setState({ kind: 'ready' });
      } catch (err) {
        // Any failure (fetch denied, parse error, WebGL context) → PNG.
        console.warn('3D preview fallback', err);
        if (!disposed) setState({ kind: 'fallback' });
      }
    })();

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [plan, fileId, modelUrl, format, paintColor, height]);

  const toggleSupports = useCallback(() => setShowSupports((v) => !v), []);

  const frameStyle: CSSProperties = {
    position: 'relative',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-separator)',
    background: '#F2F2F7',
    overflow: 'hidden',
    ...style,
  };

  const chipStyle: CSSProperties = {
    position: 'absolute',
    top: 8,
    right: 8,
    zIndex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 999,
    border: '1px solid var(--color-separator)',
    background: 'rgba(255,255,255,0.92)',
    fontSize: 'var(--fs-caption)',
    fontFamily: 'var(--font-body)',
    color: 'var(--color-text-secondary)',
    userSelect: 'none',
  };

  if (plan === 'none' && state.kind !== 'fallback') return null;
  // No byte source at all (defensive) — nothing to render.
  if (!fileId && !modelUrl) return null;

  // STEP / no-WebGL / load failure — the ME-2 PNG carries the preview.
  if (plan === 'png' || state.kind === 'fallback') {
    if (!previewUrl) return null;
    return (
      <img
        src={resolveMediaUrl(previewUrl)}
        alt={alt}
        style={{ display: 'block', width: '100%', maxHeight: height, objectFit: 'contain', ...frameStyle }}
      />
    );
  }

  return (
    <div style={{ ...frameStyle, width: '100%' }}>
      <div ref={canvasHostRef} aria-label={alt} role="img" data-phase={phase} />
      {supportsToggleVisible(supports) && (
        <button
          type="button"
          onClick={toggleSupports}
          style={{ ...chipStyle, cursor: 'pointer' }}
          data-supports-on={showSupports ? 'on' : 'off'}
        >
          Поддержки {showSupports ? '· вкл' : '· выкл'}
        </button>
      )}
      {state.kind === 'loading' && <span style={chipStyle}>Загрузка 3D…</span>}
      {state.kind === 'ready' && <span style={chipStyle}>3D · можно вращать</span>}
      {fileName != null && state.kind !== 'loading' && (
        <span style={{ ...chipStyle, right: 'auto', left: 8 }}>{fileName}</span>
      )}
    </div>
  );
}
